import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for managing focus on route changes
 * Ensures screen reader users are notified of page changes
 */
export function useFocusOnRouteChange() {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const announcerRef = useRef(null);

  useEffect(() => {
    // Only trigger on actual route changes
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;

      // Small delay to ensure the new page content is rendered
      const timeoutId = setTimeout(() => {
        // Try to focus on main content first
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
          mainContent.focus();
          return;
        }

        // Fallback to h1
        const h1 = document.querySelector('h1');
        if (h1) {
          h1.tabIndex = -1;
          h1.focus();
          return;
        }

        // Last resort: focus on body
        document.body.focus();
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname]);

  return announcerRef;
}

/**
 * Route change announcer component
 * Announces page changes to screen readers
 */
export function RouteChangeAnnouncer() {
  const location = useLocation();
  const previousPathRef = useRef(location.pathname);
  const announcementRef = useRef('');

  useEffect(() => {
    if (previousPathRef.current !== location.pathname) {
      previousPathRef.current = location.pathname;

      // Generate announcement based on route
      const pageName = getPageNameFromPath(location.pathname);
      announcementRef.current = `Navigated to ${pageName}`;

      // Clear announcement after screen reader has time to read it
      const timeoutId = setTimeout(() => {
        announcementRef.current = '';
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [location.pathname]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcementRef.current}
    </div>
  );
}

/**
 * Get human-readable page name from route path
 */
function getPageNameFromPath(path) {
  const routeNames = {
    '/': 'Home',
    '/login': 'Login',
    '/register': 'Registration',
    '/dashboard': 'Dashboard',
    '/rooms': 'Rooms',
    '/room-types': 'Room Types',
    '/guests': 'Guests',
    '/reservations': 'Reservations',
    '/quick-booking': 'Quick Booking',
    '/bulk-booking': 'Advance Booking',
    '/advance-booking': 'Advance Booking',
    '/housekeeping': 'Housekeeping',
    '/invoices': 'Invoices',
    '/reports': 'Reports',
    '/settings': 'Settings',
    '/users': 'User Management',
    '/pricing': 'Pricing',
    '/usage': 'Usage Dashboard',
    '/referrals': 'Referral Dashboard',
  };

  // Check exact match first
  if (routeNames[path]) {
    return routeNames[path];
  }

  // Check for partial matches (e.g., /rooms/123)
  for (const [route, name] of Object.entries(routeNames)) {
    if (path.startsWith(route) && route !== '/') {
      return name;
    }
  }

  // Fallback: convert path to readable format
  return path
    .split('/')
    .filter(Boolean)
    .map(segment => segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '))
    .join(' - ') || 'Page';
}

/**
 * Hook to announce dynamic content changes
 * Use this when content updates without a route change
 */
export function useAnnouncer() {
  const announcerRef = useRef(null);

  const announce = useCallback((message, priority = 'polite') => {
    if (announcerRef.current) {
      // Clear previous announcement
      announcerRef.current.textContent = '';
      
      // Set priority
      announcerRef.current.setAttribute('aria-live', priority);
      
      // Small delay to ensure the change is picked up
      requestAnimationFrame(() => {
        if (announcerRef.current) {
          announcerRef.current.textContent = message;
        }
      });
    }
  }, []);

  const announcePolite = useCallback((message) => {
    announce(message, 'polite');
  }, [announce]);

  const announceAssertive = useCallback((message) => {
    announce(message, 'assertive');
  }, [announce]);

  const AnnouncerElement = useCallback(() => (
    <div
      ref={announcerRef}
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    />
  ), []);

  return {
    announce,
    announcePolite,
    announceAssertive,
    AnnouncerElement,
  };
}

/**
 * Global live region component for toast-like announcements
 */
export function GlobalLiveRegion({ id = 'global-live-region' }) {
  return (
    <>
      {/* Polite announcements (non-urgent) */}
      <div
        id={`${id}-polite`}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      />
      {/* Assertive announcements (urgent) */}
      <div
        id={`${id}-assertive`}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
      />
    </>
  );
}

/**
 * Utility function to announce to global live region
 * Can be called from anywhere in the app
 */
export function announceToScreenReader(message, priority = 'polite') {
  const regionId = priority === 'assertive' 
    ? 'global-live-region-assertive' 
    : 'global-live-region-polite';
  
  const region = document.getElementById(regionId);
  if (region) {
    // Clear and re-announce
    region.textContent = '';
    requestAnimationFrame(() => {
      region.textContent = message;
    });
  }
}

/**
 * Loading state announcer
 */
export function LoadingAnnouncer({ isLoading, loadingMessage = 'Loading...', completeMessage = 'Content loaded' }) {
  const previousLoadingRef = useRef(isLoading);

  useEffect(() => {
    if (previousLoadingRef.current !== isLoading) {
      previousLoadingRef.current = isLoading;
      
      if (isLoading) {
        announceToScreenReader(loadingMessage, 'polite');
      } else {
        announceToScreenReader(completeMessage, 'polite');
      }
    }
  }, [isLoading, loadingMessage, completeMessage]);

  return null;
}

/**
 * Form submission announcer
 */
export function FormSubmissionAnnouncer({ status, successMessage, errorMessage }) {
  useEffect(() => {
    if (status === 'success' && successMessage) {
      announceToScreenReader(successMessage, 'polite');
    } else if (status === 'error' && errorMessage) {
      announceToScreenReader(errorMessage, 'assertive');
    }
  }, [status, successMessage, errorMessage]);

  return null;
}

/**
 * Data update announcer for tables/lists
 */
export function DataUpdateAnnouncer({ itemCount, itemType = 'items' }) {
  const previousCountRef = useRef(itemCount);

  useEffect(() => {
    if (previousCountRef.current !== itemCount && itemCount !== undefined) {
      const message = itemCount === 0 
        ? `No ${itemType} found` 
        : `${itemCount} ${itemType} ${itemCount === 1 ? 'is' : 'are'} now displayed`;
      
      announceToScreenReader(message, 'polite');
      previousCountRef.current = itemCount;
    }
  }, [itemCount, itemType]);

  return null;
}
